<template>
  <div class="page-platform-catalog">
    <div class="page-platform-catalog__content">
      <div v-if="!isPhone" class="page-admin__table-header">
        <div class="page-admin__count">{{ platforms.length }} {{ $t('shared.platforms') }}</div>
        <div class="page-admin__actions">
          <BaseRefreshButton class="page-admin__refresh" :size="40" :pending="pending" @refresh="getPlatforms" />
        </div>
      </div>

      <div class="page-platform-catalog__table">
        <TableNormal
          :headers="headers"
          :items="orderedPlatforms"
          :items-per-page="-1"
          class="elevation-1"
          :loading="pending"
          :loading-text="$t('page.admin-platforms.loading')"
          disable-sort
          hide-default-footer
          @click:row="openEditor"
        >
          <!-- Phones: the table draws the count line above its rows (docs/page-layout.md). -->
          <template #phone-count>
            <span class="number-monospace">{{ platforms.length }}</span>
            {{ ' ' + $t('shared.platforms') }}
          </template>
          <template #phone-actions>
            <BaseRefreshButton :pending="pending" @refresh="getPlatforms" />
          </template>
          <template v-slot:progress>
            <BaseLoadingBar v-if="pending" />
          </template>

          <template v-slot:item.name="{ item }">
            <div class="page-platform-catalog__platform">
              <PlatformIcon :platform="item.id" />
              <span class="page-platform-catalog__name">{{ nameOf(item) }}</span>
              <BaseTag
                v-for="tag in item.tags"
                :key="tag"
                :tone="getPlatformTagTone(tag)"
                :icon="getPlatformTagIcon(tag)"
                preset="soft"
              >
                {{ $t('tags.' + tag) }}
              </BaseTag>
            </div>
          </template>

          <template v-slot:item.id="{ item }">
            <code class="page-platform-catalog__id">{{ item.id }}</code>
          </template>

          <template v-slot:item.regions="{ item }">
            <div class="page-platform-catalog__regions">
              <BaseTag v-for="region in item.regions" :key="region.code" tone="neutral" preset="soft">
                {{ region.code }}
              </BaseTag>
            </div>
          </template>

          <template v-slot:item.description="{ item }">
            <BaseTag :tone="descriptionStatus(item) === 'complete' ? 'success' : 'warning'" preset="soft">
              {{ $t(`page.admin-platform-catalog.description-status.${descriptionStatus(item)}`) }}
            </BaseTag>
          </template>

          <template v-slot:item.subPlatforms="{ item }">
            {{ (item.subPlatforms || []).length || '' }}
          </template>

          <template v-slot:item.url="{ item }">
            <a
              v-if="item.url"
              class="page-platform-catalog__url"
              :href="item.url"
              target="_blank"
              rel="noopener noreferrer"
              @click.stop
            >
              {{ hostOf(item.url) }}
              <BaseIcon name="external-link" />
            </a>
          </template>

          <template v-slot:item.actions="{ item }">
            <div @click.stop>
              <BaseRoundButton
                :size="36"
                icon="edit-2"
                is-white
                :tooltip-text="$t('actions.edit')"
                @click.native="openEditor(item)"
              />
            </div>
          </template>
        </TableNormal>
      </div>
    </div>

    <PlatformEditorDialog ref="editor" @refresh="getPlatforms" />
  </div>
</template>

<script>
import BaseIcon from '@/components/base/BaseIcon';
import BaseLoadingBar from '@/components/base/BaseLoadingBar';
import BaseRefreshButton from '@/components/base/BaseRefreshButton.vue';
import BaseRoundButton from '@/components/base/BaseRoundButton.vue';
import BaseTag from '@/components/base/BaseTag.vue';
import PlatformEditorDialog from '@/components/page-admin-platform-catalog/PlatformEditorDialog';
import PlatformIcon from '@/components/PlatformIcon';
import TableNormal from '@/components/table/TableNormal.vue';
import { sortPlatformsByDisplayOrder } from '@/util/constants/platform-display-order';
import { LOCALE } from '@/util/i18n/locale';
import { getPlatformTagIcon, getPlatformTagTone } from '@/util/tag-tones/platform-tags';
import { viewport } from '@/util/viewport';
import { mapActions, mapGetters } from 'vuex';

/**
 * The platform catalog every tenant shares (names, tags, descriptions, regions, sub-platforms),
 * for platform admins. Which platforms a tenant sees is PageAdminPlatforms' job, not this page's.
 * Rows follow the order users see them in. No create or delete here: a platform's id has to match
 * the scrapers and the tender index, and deleting one cascades to every tenant's access rows.
 */
export default {
  name: 'PageAdminPlatformCatalog',
  components: {
    TableNormal,
    BaseIcon,
    BaseLoadingBar,
    BaseRefreshButton,
    BaseRoundButton,
    BaseTag,
    PlatformEditorDialog,
    PlatformIcon,
  },
  computed: {
    ...mapGetters('platforms', ['platforms', 'pending']),
    isPhone() {
      return viewport.size === 'phone';
    },
    orderedPlatforms() {
      return sortPlatformsByDisplayOrder(this.platforms);
    },
    headers() {
      return [
        { text: this.$t('page.admin-platform-catalog.columns.name'), value: 'name' },
        { text: this.$t('page.admin-platform-catalog.columns.id'), value: 'id', phone: 'subtitle' },
        { text: this.$t('page.admin-platform-catalog.columns.regions'), value: 'regions', phone: 'meta' },
        { text: this.$t('page.admin-platform-catalog.columns.description'), value: 'description' },
        { text: this.$t('page.admin-platform-catalog.columns.sub-platforms'), value: 'subPlatforms', align: 'end' },
        { text: this.$t('page.admin-platform-catalog.columns.url'), value: 'url' },
        { text: this.$t('page.tenders.data-table-titles.actions'), value: 'actions', align: 'end', width: 90 },
      ];
    },
  },
  methods: {
    ...mapActions('platforms', ['getPlatforms']),
    getPlatformTagTone,
    getPlatformTagIcon,
    nameOf(platform) {
      return this.$i18n.locale === LOCALE.FR_CA ? platform.nameFr : platform.nameEn;
    },
    // The description is what the platforms detail panel is for, so a missing one is worth flagging.
    descriptionStatus(platform) {
      const hasFr = !!(platform.descriptionFr || '').trim();
      const hasEn = !!(platform.descriptionEn || '').trim();
      if (hasFr && hasEn) return 'complete';
      if (hasFr) return 'missing-en';
      if (hasEn) return 'missing-fr';
      return 'missing';
    },
    hostOf(url) {
      try {
        return new URL(url).host.replace(/^www\./, '');
      } catch (error) {
        return url;
      }
    },
    openEditor(platform) {
      this.$refs.editor.openDialog(platform);
    },
  },
  created() {
    this.getPlatforms();
  },
};
</script>

<style lang="scss">
.page-platform-catalog {
  &__content {
    display: flex;
    flex: auto;
    flex-direction: column;
    padding: 32px;
  }

  &__table {
    .v-data-table.theme--light tbody tr {
      cursor: pointer;
    }

    .v-data-table.theme--light tbody td {
      color: $color-normal;
    }

    .v-data-table__progress .base-loading-bar {
      position: absolute;
      top: 48px;
    }
  }

  &__platform {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    padding: 8px 0;
  }

  &__name {
    margin: 0 6px 0 2px;
    font-size: 15px;
  }

  &__id {
    padding: 1px 6px;
    border-radius: $radius-sm;
    background: $color-slate-50;
    color: $color-slate-500;
    font-size: 12px;
    white-space: nowrap;
  }

  &__regions {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }

  &__url {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: $color-accent;
    font-size: 13px;
    text-decoration: none;
    white-space: nowrap;

    &:hover {
      text-decoration: underline;
    }

    .feather {
      width: 12px;
      height: 12px;
    }
  }
}
</style>
